<template>
  <div :class="`relative ${width} ${maxWidthClass} ${minWidthClass}`">
    <div
      v-if="canReset"
      class="absolute right-0 text-xs cursor-pointer bottom-full text-main-primary"
      @click="
        deselectOptions();
        $emit('enable');
      "
    >
      Reset Filter
    </div>
    <!-- Select  -->
    <button
      :ref="`dropdownInput-${uid}`"
      :class="`flex ${width} ${maxWidthClass} ${minWidthClass} items-center justify-between border leading-tight ${classes} ${error ? 'border-red-500' : 'border-dashboard-grey'} ${topLabel ? ' px-3 py-1.5 ' : 'p-3'
      } disabled:bg-zinc-300 disabled:cursor-not-allowed disabled:pointer-events-none cursor-pointer bg-white focus:shadow-outline select-none rounded-md  placeholder-slate-300 shadow-sm outline-none focus:border-main-primary focus:ring-main-primary`"
      :disabled="inputIsDisabled"
      @click.prevent="toggleDropdown"
    >
      <div
        v-if="topLabel"
        class="flex flex-col items-start gap-1 text-xs font-normal truncate"
      >
        <span
          class="text-[#707C8ECC]"
          :title="inputText"
        >{{ topLabel }}</span>
        <span
          :class="`mr-2 font-normal truncate text-primary ${inputIsDisabled ? 'text-[#707C8ECC]' : 'text-primary'}${(disableLocalization && inputText !== emptySelectionText && inputText !== 'Please select...') ? ' no-localize-translate' : ''}`"
          :title="inputText"
        >
          <slot name="icon" />

          {{ inputText }}
        </span>
      </div>
      <span
        v-else
        :class="`mr-2 font-normal truncate text-primary ${inputIsDisabled ? 'text-[#707C8ECC]' : 'text-primary'}${(disableLocalization && inputText !== emptySelectionText && inputText !== 'Please select...') ? ' no-localize-translate' : ''}`"
        :title="inputText"
      >{{ inputText }}</span>
      <div class="flex gap-2">
        <span class="text-gray-300">|</span>
        <ChevronDownSmall
          class="text-gray-500 duration-150 transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </button>

    <!-- Dropdown options -->
    <div
      v-show="isOpen"
      :ref="`dropdownList-${uid}`"
      class="fixed z-10 mt-2 overflow-y-auto bg-white border-2 border-gray-200 shadow-lg select-none max-h-48"
      :style="{ top: dropdownTop + 'px', left: dropdownLeft + 'px', width: dropdownWidth + 'px' }"
    >
      <div class="sticky top-0 bg-white">
        <div class="relative py-1 pl-3 pr-2 overflow-hidden border-b border-transparent border-b-gray-300">
          <div class="absolute left-4 top-3">
            <SearchIcon class="w-5 h-5 mx-auto text-slate-600" />
          </div>
          <input
            v-model="searchQuery"
            type="text"
            class="w-full p-2 pl-8 text-sm border-transparent border-none focus:shadow-outline ring-0 focus:border-transparent focus:ring-0"
            placeholder="Search..."
          >
          <button
            v-if="searchQuery"
            type="button"
            class="absolute right-4 top-1.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-green-light/80 text-main-primary hover:text-gray-700 focus:outline-none"
            @click.stop="clearInput"
          >
            <TimesSmall custom-style="w-4 h-4" />
          </button>
        </div>
      </div>
      <ul
        :ref="`dropdownListWrapper-${uid}`"
        class="bg-white"
      >
        <template v-if="filteredOptions.length">
          <li
            v-if="emptySelectionText"
            class="px-4 py-2 text-sm hover:bg-main-primary hover:text-white"
            :title="emptySelectionText"
            @click="deselectOptions"
          >
            {{ emptySelectionText }}
          </li>
          <li
            v-for="(option, index) in filteredOptions"
            :key="`option-${index}`"
            :title="displayKey ? option[displayKey] : option"
            class="px-4 py-2 text-sm hover:text-main-primary"
            :class="{ 'pointer-events-none bg-main-primary text-white': inputTextValue === (valueKey ? option[valueKey] : option), 'no-localize-translate': !translateLabels }"
            @click="selectOption(option)"
          >
            {{ displayKey ? option[displayKey] : option }}
          </li>
        </template>
        <span :id="`hiddenElement-${uid}`" />
        <div
          v-if="loading"
          class="flex justify-center py-2"
        >
          <AnimatedCircleLoader />
        </div>
        <p
          v-if="!filteredOptions.length && !loading"
          class="px-4 py-2 text-sm text-gray-500"
        >
          No results found.
        </p>
      </ul>
      <div v-if="!listingOtherOptions">
        <button
          v-show="searchMoreText"
          type="button"
          class="w-full py-2.5 text-center text-main-primary hover:bg-main-primary hover:text-slate-50"
          @click.stop="listOtherOptions"
        >
          {{ searchMoreText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'DropDownInput',

  props: {
    searchMoreText: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    endSearch: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: Boolean,
      default: true,
    },
    emptySelectionText: {
      type: String,
      default: null,
    },
    topLabel: {
      type: String,
      default: null,
    },
    firstValue: {
      type: [String, Number],
      default: null,
    },
    classes: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      default: 'w-full',
    },
    maxWidthClass: {
      type: String,
      default: 'max-w-[300px]',
    },
    minWidthClass: {
      type: String,
      default: 'min-w-[140px]',
    },
    valueKey: {
      type: String,
      default: null,
    },
    displayKey: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    resetAsFilter: {
      type: Boolean,
      default: false,
    },
    translateLabels: {
      type: Boolean,
      default: false,
    },
    disableLocalization: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['valueUpdated', 'searchQuery', 'enable'],

  data() {
    return {
      uid: null,
      isOpen: false,
      dropdownTop: 0,
      dropdownLeft: 0,
      dropdownWidth: 0,
      errorMessage: this.error,
      searchQuery: '',
      firstValueUpdated: false,
      selectedOption: null,
      ignoreFirstValue: false,
      inputIsDisabled: this.disabled,
      allOptions: this.options,
      listedOptionsBeforeOtherOptions: this.options,
      listingOtherOptions: false,
      searchPage: 1,
      dataLoading: this.loading,
      stopSearchingOtherOptions: this.endSearch,
      observer: null,
      emittedValue: null,
    }
  },

  computed: {
    firstValueEmpty() {
      return !this.firstValue || this.ignoreFirstValue
    },
    inputText() {
      const defaultText = this.emptySelectionText ? this.emptySelectionText : 'Please select...'
      if (!this.firstValueUpdated && this.firstValue) {
        if (this.displayKey) {
          if (this.allOptions.length) {
            const stringToDisplay = this.allOptions.find(option => option[this.valueKey] === this.firstValue)
            return stringToDisplay ? stringToDisplay[this.displayKey] : defaultText
          }
        }
        else {
          return this.firstValue
        }
      }
      if (this.firstValueUpdated) {
        return this.valueKey ? this.selectedOption[this.displayKey] : this.selectedOption
      }
      return defaultText
    },
    inputTextValue() {
      const defaultText = ''
      if (!this.allOptions.length || (this.allOptions.length && !this.firstValueUpdated && this.firstValueEmpty)) {
        return this.emptySelectionText || defaultText
      }
      if (this.firstValueUpdated) {
        return this.valueKey ? this.selectedOption[this.valueKey] : this.selectedOption
      }
      if (!this.firstValueUpdated && this.firstValue) {
        if (this.displayKey && this.allOptions.length) {
          const stringToDisplay = this.allOptions.find(option => option[this.valueKey] === this.firstValue)
          return stringToDisplay ? stringToDisplay[this.displayKey] : defaultText
        }
        else {
          return this.firstValue
        }
      }
      return defaultText
    },
    filteredOptions() {
      if (this.searchQuery && !this.listingOtherOptions) {
        return [...this.allOptions].filter(option => (this.displayKey ? String(option[this.displayKey]).toLowerCase().includes(this.searchQuery.toLowerCase()) : typeof option === 'string' ? option.toLowerCase().includes(this.searchQuery.toLowerCase()) : option.toString().includes(this.searchQuery)))
      }
      else if (this.displayKey === this.valueKey && this.allOptions.length) {
        return [...this.allOptions].sort()
      }
      else {
        return this.displayKey && this.sort ? [...this.allOptions].sort((a, b) => String(a[this.displayKey]).localeCompare(String(b[this.displayKey]))) : [...this.allOptions].sort()
      }
    },
    canReset() {
      return !this.isOpen && this.resetAsFilter && this.firstValueUpdated && !['all', ''].includes(this.emittedValue.toLowerCase())
    },
  },

  watch: {
    isOpen(val) {
      // Use $nextTick to wait until Vue's next update cycle before executing the callback.
      // This ensures that the DOM is updated and the dropdown is fully rendered before the event listener is added.
      nextTick(() => {
        if (val) {
          document.body.addEventListener('click', this.handleOutsideClick)
          document.body.addEventListener('keydown', this.handleEscapeKey)
          this.positionDropdown()
          this.positionDropdown = debounce(this.positionDropdown, 50)
          document.addEventListener('scroll', this.positionDropdown, true)
        }
        else {
          document.body.removeEventListener('click', this.handleOutsideClick)
        }
      })
    },
    emptySelectionText: {
      immediate: true,
      handler(newText) {
        if (newText) {
          this.$emit('valueUpdated', '')
          this.emittedValue = ''
        }
      },
    },
    options: {
      deep: true,
      handler(newOptions) {
        this.allOptions = this.listingOtherOptions && this.searchPage > 1 ? [...this.allOptions, ...newOptions] : newOptions
      },
    },
    firstValue(newValue) {
      const option = this.displayKey ? this.allOptions.find(option => option[this.valueKey] === newValue) : newValue
      this.selectOption(option, false)
    },
    disabled(isDisabled) {
      this.inputIsDisabled = isDisabled
    },
    searchQuery(newQuery) {
      if (this.listingOtherOptions) {
        if (!newQuery) {
          this.allOptions = this.listedOptionsBeforeOtherOptions
          this.listingOtherOptions = false
        }
        if (newQuery) {
          this.ignoreFirstValue = true
          this.searchPage = 1
          this.allOptions = []
        }
        this.observer.unobserve(document.getElementById(`hiddenElement-${this.uid}`))
        this.debouncedEmitSearchQuery(newQuery, this.searchPage)
      }
    },
    loading(isLoading) {
      this.dataLoading = isLoading
    },
    filteredOptions(newOptions) {
      if (this.listingOtherOptions && newOptions.length) {
        this.observer.observe(document.getElementById(`hiddenElement-${this.uid}`))
      }
    },
    endSearch(hasSearchEnded) {
      this.stopSearchingOtherOptions = hasSearchEnded
    },
  },

  created() {
    this.debouncedEmitSearchQuery = debounce((query, page) => {
      this.$emit('searchQuery', { query, page })
    }, 300)
  },

  mounted() {
    this.uid = this.$.uid
    this.observer = new IntersectionObserver(this.onElementObserved, {
      root: this.$refs[`dropdownListWrapper-${this.uid}`],
      threshold: 1.0,
    })
  },

  beforeUnmount() {
    this.observer.disconnect()
  },

  methods: {
    positionDropdown() {
      const dropdownInput = this.$refs[`dropdownInput-${this.uid}`]
      const dropdownList = this.$refs[`dropdownList-${this.uid}`]

      if (!dropdownInput || !dropdownList) return

      const { top, right, left } = dropdownInput.getBoundingClientRect()

      // if we didn't use nextTick, the dropdownList width and height would be 0
      const { height: dropdownListHeight, width: dropdownListWidth } = dropdownList.getBoundingClientRect()
      const { height: dropdownInputHeight, width: dropdownInputWidth } = dropdownInput.getBoundingClientRect()
      const { innerHeight, innerWidth } = window

      if (top + dropdownInputHeight + dropdownListHeight > innerHeight) {
        this.dropdownTop = top - dropdownListHeight
      }
      else {
        this.dropdownTop = top + dropdownInputHeight
      }

      if (left + dropdownListWidth > innerWidth) {
        this.dropdownLeft = innerWidth - dropdownListWidth - 10
      }
      else {
        this.dropdownLeft = right - dropdownInputWidth
      }

      this.dropdownWidth = dropdownInputWidth
    },
    handleOutsideClick(event) {
      // If the clicked target is not within the dropdown list or the dropdown input,
      // set isOpen to false to close the dropdown and remove the click event listener from the document.

      if (!this.$refs[`dropdownList-${this.uid}`] || !this.$refs[`dropdownInput-${this.uid}`]) return

      if (!this.$refs[`dropdownList-${this.uid}`].contains(event.target) && !this.$refs[`dropdownInput-${this.uid}`].contains(event.target)) {
        this.closeDropdownAndkillAllListeners()
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.closeDropdownAndkillAllListeners()
      }
    },
    killAllListeners() {
      document.body.removeEventListener('click', this.handleOutsideClick)
      document.body.removeEventListener('keydown', this.handleEscapeKey)
      document.removeEventListener('scroll', this.positionDropdown, true)
    },
    closeDropdownAndkillAllListeners() {
      this.closeModal()
      this.killAllListeners()
    },
    closeModal() {
      this.isOpen = false
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen
      nextTick(() => {
        if (!this.isOpen) {
          this.killAllListeners()
        }
      })
    },
    onElementObserved(entries) {
      const { target, isIntersecting } = entries[0]
      if (!isIntersecting || this.stopSearchingOtherOptions) return
      this.observer.unobserve(target)
      this.searchPage++
      this.$emit('searchQuery', { query: this.searchQuery, page: this.searchPage })
    },
    deselectOptions() {
      this.firstValueUpdated = false
      this.ignoreFirstValue = true
      this.$emit('valueUpdated', '')
      this.emittedValue = ''
      this.closeDropdownAndkillAllListeners()
    },
    selectOption(option, notify = true) {
      if (option === undefined) return
      this.selectedOption = option
      this.firstValueUpdated = true
      this.ignoreFirstValue = false
      if (notify) {
        const value = this.displayKey ? option[this.valueKey] : option
        this.$emit('valueUpdated', value)
        this.emittedValue = value
      }
      this.closeDropdownAndkillAllListeners()
    },
    clearInput() {
      this.searchQuery = ''
    },
    listOtherOptions() {
      // First time allowing to search other options (from an API for example)
      this.allOptions = []
      this.listingOtherOptions = true
      this.ignoreFirstValue = true
      this.$emit('valueUpdated', '')
      this.emittedValue = ''
      this.$emit('searchQuery', { query: this.searchQuery, page: this.searchPage })
    },
  },
}
</script>
